import {
    Autocomplete, Button, Card, CircularProgress, Dialog, DialogContent,
    DialogTitle, Divider, FormHelperText, Grid, IconButton, TextField
} from "@mui/material";
import { IconInfoCircle, IconX } from "@tabler/icons-react";
import { removeEmptyKeys } from "helper/encodeData";
import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { ErrorAlert } from "ui-component/custom-components/alertMessage";
import { Required } from "ui-component/custom-components/mandatory";
import debounce from "lodash.debounce";
import { getStateOptionCreate } from "store/app/form-management/district-configuration/actions";
import VWDropdown from "components/dropdown";
import {
    getRoleOptions,
    getSelectedStates,
    userCreate,
    userCreateErrorClear
} from "store/app/user-management/actions";
import { unionBy } from 'lodash';
import { getRegionOption } from "store/app/farm-management/farm-records/actions";

const styles = {
    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
        display: "none",
    },
    "& input[type=number]": {
        MozAppearance: "textfield",
    },
}

const CreateUser = (props) => {

    const { open, filter, paginationData, errorMessage, onClose, isPending } = props;

    const [isAutoOpen, setIsAutoOpen] = useState();
    const [userData, setUserData] = useState();
    const [errors, setErrors] = useState({});
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRole, setSelectedRole] = useState([]);

    //const loading = open && (options && options.length === 0);

    useEffect(() => {
        props.userCreateErrorClear();
        setUserData();
        setErrors({});
        setSelectedState([]);
    }, [open])

    //--------------------Region--------------------------------------
    const [regionOptions, setRegionOptions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState([]);

    const loadRegionOptions = (filter, page, pageSize) => {
        return props.getRegionOption(filter, "create", page, pageSize)
    }

    //-------------------------State------------------------
    const [stateOptions, setStateOptions] = useState([]);
    const [selectedState, setSelectedState] = useState([]);
    const [stateKey, setStateKey] = useState(0);

    const loadStateOption = (filter, page, pageSize) => {
        return props.getStateOption(filter, selectedRegion, page, pageSize)
    }

    const loadRoleOptions = (filter) => {
        setLoading(true);
        props.getRoleOptions(filter)
            .then((results) => {
                setOptions(results);
                setLoading(false);
            })
            .catch(() => {
                setOptions([]);
                setLoading(false);
            })
    }

    const validateOnchange = (e) => {
        const { name, value } = e.target;
        let result = {};

        if (name === 'email') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            result = {
                isValid: !emailRegex.test(value),
                message: 'Enter valid email address',
            };
        }
        if (name === 'mobileNumber' && value) {
            const contactNumberRegex = /^\d{10}$/;
            result = {
                isValid: !contactNumberRegex.test(value),
                message: 'Enter valid mobile number',
            };
        }

        setUserData({
            ...userData,
            [name]: value
        })
        setErrors({
            ...errors,
            [name]: result.isValid ? result.message : null,
        })
    }

    const handleRoleSelect = (e, option) => {
        const { value } = option || {};
        setSelectedRole(option);
        setSelectedState([]);
        setSelectedRegion([]);
        setUserData({
            ...userData,
            roleId: value,
            stateIds: []
        })
        setErrors({
            ...errors,
            roleId: null,
            stateIds: null
        })
    }

    const handleRegionSelect = (options, name, reason, details) => {
        const { option } = details || {};
        const Ids = options?.map((state) => state.value);
        setSelectedRegion(options);
        setStateKey(prev => prev + 1);
        setErrors({
            ...errors,
            stateIds: null,
        })
        const existingRegionId = selectedRegion?.map((region) => region.value);
        const isNew = Ids?.filter((data) => !existingRegionId?.includes(data));
        const isRemoved = existingRegionId?.filter((data) => !Ids?.includes(data));
        //Checking added new
        if (isNew?.length === 1) {
            props.getSelectedStates([option?.value]).then((states) => {
                setSelectedState((prev) => unionBy(prev, states, "value"));
                const stateIdArr = states?.map((id) => id.value);
                setUserData({
                    ...userData,
                    stateIds: [...stateIdArr, ...userData.stateIds]
                })
            })
        }
        //Checking removed old
        else if (isRemoved?.length > 0) {
            const filteredStates = selectedState?.filter((state) => !isRemoved?.includes(state.regionId));
            setSelectedState(filteredStates);
            setUserData({
                ...userData,
                stateIds: filteredStates?.map((state) => state.value)
            })
        }
        else {
            return null;
        }
    }


    const handleStateSelect = (options) => {
        const Ids = options?.map((state) => state.value);
        setSelectedState(options);
        setUserData({
            ...userData,
            stateIds: Ids
        })
        setErrors({
            ...errors,
            stateIds: null,
        })
    }

    //---------------FILTER DROP DOWN------------------------------
    const debouncedFetchOptions = useMemo(() => debounce((query) => loadRoleOptions(query), 500), []);

    const handleFilter = (e) => {
        const { value } = e.target || {};
        debouncedFetchOptions(value);
    };

    const handleChangePassword = (e) => {
        e.preventDefault();

        const { firstName, displayName, email, roleId, stateIds } = userData || {};
        const currentErrors = {};

        if (!firstName && !firstName?.trim()) {
            currentErrors.firstName = 'First name is required';
        }
        if (!displayName && !displayName?.trim()) {
            currentErrors.displayName = 'Display name is required';
        }
        if (!email && !email?.trim()) {
            currentErrors.email = 'Email address is required';
        }
        if (errors && errors.email) {
            currentErrors.email = 'Enter valid email address';
        }
        if (!roleId && !roleId?.trim()) {
            currentErrors.roleId = 'Role is required';
        }
        if ((selectedRegion?.length > 0) && (stateIds?.length === 0)) {
            currentErrors.stateIds = 'State/Export is required';
        }

        setErrors(currentErrors);

        if (Object.keys(currentErrors).length === 0) {
            props.userCreate(userData, filter, paginationData);
        }
    }

    const validateClose = (e, reason) => {
        if (reason !== 'backdropClick') {
            onClose();
        }
    }

    return (
        <>
            <Dialog open={open} maxWidth={'xs'} fullWidth onClose={validateClose}>
                {open && (
                    <>
                        <Card sx={{ overflow: 'auto' }} >
                            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: "center", pt: 0, pb: 0 }}>
                                Create User
                                <IconButton
                                    aria-label="close"
                                    onClick={onClose}
                                >
                                    <IconX />
                                </IconButton>
                            </DialogTitle>
                            <Divider />
                            <DialogContent>
                                {errorMessage && <ErrorAlert content={errorMessage} />}
                                <form>
                                    <Grid container rowGap={4} marginTop={2}>
                                        <Grid container rowGap={3} justifyContent={'center'} direction={'column'}>
                                            <Grid item >
                                                <TextField
                                                    autoFocus
                                                    label={<div >First Name <Required /></div>}
                                                    fullWidth
                                                    size="small"
                                                    name='firstName'
                                                    type={'text'}
                                                    onChange={validateOnchange}
                                                    error={errors?.firstName}
                                                    helperText={errors?.firstName}
                                                />
                                            </Grid>
                                            <Grid item >
                                                <TextField
                                                    label={<>Last Name</>}
                                                    fullWidth
                                                    size="small"
                                                    name='lastName'
                                                    type={'text'}
                                                    onChange={validateOnchange}
                                                    error={errors?.lastName}
                                                    helperText={errors?.lastName}
                                                />
                                            </Grid>
                                            <Grid item >
                                                <TextField
                                                    label={<>Display Name <Required /></>}
                                                    fullWidth
                                                    size="small"
                                                    name='displayName'
                                                    type={'text'}
                                                    onChange={validateOnchange}
                                                    error={errors?.displayName}
                                                    helperText={errors?.displayName}
                                                />
                                            </Grid>
                                            <Grid item >
                                                <TextField
                                                    label={<>Email Address<Required /></>}
                                                    fullWidth
                                                    size="small"
                                                    name='email'
                                                    type={'text'}
                                                    onChange={validateOnchange}
                                                    error={errors?.email}
                                                    helperText={errors?.email}
                                                />
                                            </Grid>
                                            <Grid item>
                                                <TextField
                                                    label={<>Mobile Number </>}
                                                    sx={styles}
                                                    fullWidth
                                                    size="small"
                                                    name='mobileNumber'
                                                    type='number'
                                                    onChange={validateOnchange}
                                                    onKeyDown={
                                                        (e) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()
                                                    }
                                                    error={errors?.mobileNumber}
                                                    helperText={errors?.mobileNumber}
                                                />
                                            </Grid>
                                            <Autocomplete
                                                size="small"
                                                disablePortal
                                                options={options}
                                                open={isAutoOpen}
                                                loading={loading}
                                                onOpen={() => {
                                                    setIsAutoOpen(true);
                                                    loadRoleOptions();
                                                }}
                                                onClose={() => {
                                                    setIsAutoOpen(false);
                                                }}
                                                onChange={handleRoleSelect}
                                                onInputChange={handleFilter}
                                                renderInput={(params) =>
                                                    <TextField
                                                        {...params}
                                                        label={<>Role <Required /></>}
                                                        error={errors?.roleId}
                                                        helperText={errors?.roleId}
                                                    />
                                                }
                                                renderOption={(props, option) => (
                                                    <li {...props} key={option?.value}>
                                                        {option?.label}
                                                    </li>
                                                )}
                                            />
                                            <Grid item>
                                                <VWDropdown
                                                    label="Assign Region"
                                                    name="regionName"
                                                    multiple={true}
                                                    // disablePortal={true}
                                                    getOptions={loadRegionOptions}
                                                    onSelect={handleRegionSelect}
                                                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                                    selectedOption={selectedRegion}
                                                    options={regionOptions}
                                                    setOptions={(options, action) => {
                                                        if (action === "clear") {
                                                            setRegionOptions([]);
                                                        }
                                                        else {
                                                            setRegionOptions((prev) => unionBy(prev, options, "value"));
                                                        }
                                                    }}
                                                    isDisabled={!selectedRole || selectedRole?.label !== "User"}
                                                    selectedLimit="skip"
                                                />
                                            </Grid>
                                            <Grid item>
                                                <VWDropdown
                                                    key={stateKey}
                                                    label="Assign State/Export"
                                                    name="stateName"
                                                    multiple={true}
                                                    // disablePortal={true}
                                                    getOptions={loadStateOption}
                                                    onSelect={handleStateSelect}
                                                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                                    selectedOption={selectedState}
                                                    options={stateOptions}
                                                    setOptions={(options, action) => {
                                                        if (action === "clear") {
                                                            setStateOptions([]);
                                                        }
                                                        else {
                                                            setStateOptions((prev) => unionBy(prev, options, "value"));
                                                        }
                                                    }}
                                                    isDisabled={!selectedRole || selectedRole?.label !== "User"}
                                                    selectedLimit="skip"
                                                    error={errors?.stateIds}
                                                />
                                                {(!selectedRole || selectedRole?.label !== "User") &&
                                                    <FormHelperText sx={{ display: "flex", gap: "3px", alignItems: "top" }}>
                                                        <IconInfoCircle size={20} />
                                                        <span>
                                                            State/Export is only selectable for the  &apos;User&apos; role. For other roles, it will be assigned automatically.
                                                        </span>
                                                        {/* <span>
                                                            State/Export is only available for the &apos;User&apos; role.
                                                        </span> */}
                                                    </FormHelperText>}
                                            </Grid>
                                        </Grid>
                                        <Grid container justifyContent={'right'}>
                                            <Button
                                                variant="contained"
                                                onClick={handleChangePassword}
                                                size="small"
                                                color="primary"
                                                disabled={isPending || Object.keys(removeEmptyKeys(errors))?.length > 0}
                                            >
                                                {isPending ? <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <CircularProgress size={'20px'} color='inherit' />
                                                    Loading...
                                                </div>
                                                    : 'Submit'
                                                }
                                            </Button>
                                            <Button
                                                variant="contained"
                                                onClick={onClose}
                                                sx={{ ml: 2 }}
                                                color='error'
                                                size="small"
                                            >
                                                Close
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </form>
                            </DialogContent>
                        </Card>
                    </>
                )
                }

            </Dialog >

        </>
    )
}

const mapStateToProps = (state) => ({
    isPending: state.users.isUserCreatePending,
    errorMessage: state.users.userCreateErrorMessage,
})
const mapDispatchToProps = (dispatch) => ({
    userCreate: (formData, filter, paginationData) => dispatch(userCreate(formData, filter, paginationData)),
    userCreateErrorClear: () => dispatch(userCreateErrorClear()),
    getRoleOptions: (filter) => dispatch(getRoleOptions(filter)),
    getStateOption: (filter, selectedRegion, page, pageSize) => dispatch(getStateOptionCreate(filter, selectedRegion, page, pageSize)),
    getRegionOption: (filter, type, page, pageSize) => dispatch(getRegionOption(filter, type, page, pageSize)),
    getSelectedStates: (filter) => dispatch(getSelectedStates(filter)),
})

export default connect(mapStateToProps, mapDispatchToProps)(CreateUser);